import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useState, useEffect } from "react";
import { useStudioPlan } from "../hooks/useStudioPlan";
import UpgradePrompt from "../components/UpgradePrompt";
import { GatedPageLayout } from "../components/PlanGatedPage";
import { ArrowRightIcon, KeyIcon } from "@heroicons/react/24/outline";

function SetupStep({
  title,
  description,
  action,
}: {
  title: string;
  description: string;
  action?: React.ReactNode;
}) {
  return (
    <li className="flex gap-4 pb-6 last:pb-0">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 border-indigo-600 bg-white dark:bg-gray-800">
        <span className="h-2.5 w-2.5 rounded-full bg-indigo-600" />
      </div>
      <div className="min-w-0 flex-1 pt-0.5">
        <p className="text-sm font-semibold text-gray-900 dark:text-white">
          {title}
        </p>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          {description}
        </p>
        {action && <div className="mt-3">{action}</div>}
      </div>
    </li>
  );
}

export default function KioskMode() {
  const navigate = useNavigate();
  const { getAuthToken } = useAuth();
  const { hasCapability, loading: planLoading } = useStudioPlan();
  const [hasKioskPassword, setHasKioskPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkKioskPassword = async () => {
      const authToken = getAuthToken();
      if (!authToken) {
        setIsLoading(false);
        return;
      }

      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/supabase/studios/me`,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        );

        if (response.ok) {
          const studioData = await response.json();
          setHasKioskPassword(!!studioData.kiosk_password_hash);
        }
      } catch (error) {
        // Error checking kiosk password
      } finally {
        setIsLoading(false);
      }
    };

    checkKioskPassword();
  }, [getAuthToken]);

  const handleEnterKiosk = () => {
    sessionStorage.setItem("lockStudentSignIn", "true");
    navigate("/app/student-sign-in");
  };

  if (planLoading || isLoading) {
    return (
      <GatedPageLayout
        title="Student Sign-in"
        subtitle="Self check-in kiosk for your front desk"
      >
        <div className="flex justify-center py-16">
          <div className="animate-pulse flex space-x-4 items-center">
            <div className="h-5 w-5 bg-indigo-200 dark:bg-indigo-600 rounded-full animate-spin" />
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Loading student sign-in...
            </div>
          </div>
        </div>
      </GatedPageLayout>
    );
  }

  if (!hasCapability("kiosk")) {
    return (
      <GatedPageLayout
        title="Student Sign-in"
        subtitle="Self check-in kiosk for your front desk"
      >
        <UpgradePrompt capability="kiosk" hideTitle />
      </GatedPageLayout>
    );
  }

  return (
    <GatedPageLayout
      title="Student Sign-in"
      subtitle="Self check-in kiosk for your front desk"
    >
      <div className="mx-auto max-w-md">
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 shadow-sm">
          <div className="mb-5 flex items-center gap-2">
            <KeyIcon
              className="h-5 w-5 text-indigo-600 dark:text-indigo-400"
              aria-hidden="true"
            />
            <h2 className="text-base font-semibold text-gray-900 dark:text-white">
              Get started
            </h2>
          </div>

          <ol>
            <SetupStep
              title="Set your kiosk password"
              description="Choose a password staff will use to exit kiosk mode. You can update it anytime in Security settings."
              action={
                !hasKioskPassword ? (
                  <Link
                    to="/app/settings/security#kiosk-password-section"
                    className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                  >
                    Go to Security Settings
                    <ArrowRightIcon className="h-4 w-4" aria-hidden="true" />
                  </Link>
                ) : undefined
              }
            />
            <SetupStep
              title="Launch kiosk mode on your device"
              description="Open this page on your front-desk tablet, then enter kiosk mode to start student check-ins."
            />
          </ol>

          <button
            type="button"
            onClick={handleEnterKiosk}
            disabled={!hasKioskPassword}
            className={`mt-2 w-full rounded-md py-2.5 text-base font-semibold ${
              hasKioskPassword
                ? "bg-indigo-600 text-white hover:bg-indigo-700"
                : "cursor-not-allowed bg-gray-300 text-gray-500 dark:bg-gray-600 dark:text-gray-400"
            }`}
          >
            Enter Kiosk Mode
          </button>
        </div>
      </div>
    </GatedPageLayout>
  );
}
